import { FLAGS } from '@gozoki/feature-flags'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { PrevisionsStoreSelector } from '../previsions/-previsions-store-selector'
import { ProductCategorySelector } from './-product-category-selector'
import { DAY_PRESETS, shortTermSearchSchema } from './-search-schema'
import { ShortTermBacktest } from './-short-term-backtest'
import {
  buildBacktestTree,
  buildForecastTree,
  formatFrDate,
  type ProductNode,
} from './-short-term-data'
import { ShortTermExplainModal } from './-short-term-explain-modal'
import { ShortTermTable } from './-short-term-table'

const TABS: AppTab<'forecast' | 'backtest'>[] = [
  { value: 'forecast', label: 'Prévision' },
  { value: 'backtest', label: 'Backtest' },
]

export const Route = createFileRoute('/_authenticated/previsions-produits/')({
  validateSearch: shortTermSearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  component: ShortTermPrevisionPage,
})

withRoutePermissions(Route)

function ShortTermPrevisionPage() {
  const enabled = useFeatureFlag(FLAGS.shortTermPrevision)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [explained, setExplained] = useState<{ product: ProductNode; storeId: number } | null>(null)
  const [asOfDraft, setAsOfDraft] = useState(search.asOf ?? '')

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const commitAsOf = () => {
    const next = asOfDraft || undefined
    if (next !== search.asOf) setSearch({ asOf: next })
  }

  const { data: stores = [] } = useQuery(api.forecast.forecastStores.queryOptions({}))
  const { data: categories = [] } = useQuery(
    api.forecast.productCategories.queryOptions({}, { enabled }),
  )

  const commonQuery = {
    storeIds: search.storeIds,
    days: search.days,
    ...(search.productIds.length > 0 ? { productIds: search.productIds } : {}),
  }

  const forecastQuery = useQuery(
    api.forecast.shortTermForecast.queryOptions(
      { query: commonQuery },
      { enabled: enabled && search.storeIds.length > 0 && search.tab === 'forecast' },
    ),
  )

  const backtestQuery = useQuery(
    api.forecast.shortTermBacktest.queryOptions(
      { query: { ...commonQuery, ...(search.asOf ? { asOf: search.asOf } : {}) } },
      { enabled: enabled && search.storeIds.length > 0 && search.tab === 'backtest' },
    ),
  )

  const storeLabelOf = (id: number) =>
    stores.find((store) => store.storeId === id)?.label ?? `Boutique ${id}`
  const categoryNameOf = (id: number) =>
    categories.find((category) => category.id === id)?.name ?? 'Sans catégorie'

  const tree = buildForecastTree(forecastQuery.data?.rows ?? [], storeLabelOf, categoryNameOf)
  const backtestTree = buildBacktestTree(
    backtestQuery.data?.rows ?? [],
    storeLabelOf,
    categoryNameOf,
  )

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <Title>Prévisions court terme UVC</Title>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  return (
    <>
      <AppHeader>
        <Title>Prévisions court terme UVC</Title>
      </AppHeader>
      <AppContainer>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <PrevisionsStoreSelector
              selectedIds={search.storeIds}
              onApply={(storeIds) => setSearch({ storeIds })}
              placeholder="Sélectionner des boutiques"
              title="Boutiques à prévoir"
            />

            <ProductCategorySelector
              selectedProductIds={search.productIds}
              onApply={(productIds) => setSearch({ productIds })}
            />

            <div className="flex items-center gap-1">
              {DAY_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  variant={search.days === preset ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSearch({ days: preset })}
                >
                  {preset}j
                </Button>
              ))}
              <Input
                type="number"
                min={1}
                max={31}
                className="h-8 w-16"
                value={search.days}
                onChange={(event) => {
                  const value = Number(event.target.value)
                  if (Number.isFinite(value) && value >= 1 && value <= 31)
                    setSearch({ days: value })
                }}
              />
            </div>
          </div>

          {search.storeIds.length === 0 ? (
            <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
          ) : (
            <AppTabs tabs={TABS} value={search.tab} onTabChange={(tab) => setSearch({ tab })}>
              <TabsContent value="forecast">
                {forecastQuery.data && (
                  <p className="mb-4 text-sm text-muted-foreground">
                    Prévision des ventes du{' '}
                    <strong className="text-foreground">
                      {formatFrDate(forecastQuery.data.startDate)}
                    </strong>{' '}
                    au{' '}
                    <strong className="text-foreground">
                      {formatFrDate(forecastQuery.data.endDate)}
                    </strong>{' '}
                    ({forecastQuery.data.horizonDays} jour
                    {forecastQuery.data.horizonDays > 1 ? 's' : ''}).
                  </p>
                )}
                {(() => {
                  if (forecastQuery.isPending) {
                    return <p className="text-muted-foreground">Calcul de la prévision…</p>
                  }
                  if (tree.length === 0) {
                    return (
                      <p className="text-muted-foreground">
                        Aucune vente sur la période pour ce périmètre.
                      </p>
                    )
                  }
                  return (
                    <ShortTermTable
                      stores={tree}
                      horizonDays={forecastQuery.data?.horizonDays ?? search.days}
                      onExplain={(product, storeId) => setExplained({ product, storeId })}
                    />
                  )
                })()}
              </TabsContent>

              <TabsContent value="backtest">
                <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <span>Se placer le</span>
                  <Input
                    type="date"
                    className="h-8 w-40"
                    value={asOfDraft}
                    onChange={(event) => setAsOfDraft(event.target.value)}
                    onBlur={commitAsOf}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') commitAsOf()
                    }}
                  />
                  {search.asOf && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAsOfDraft('')
                        setSearch({ asOf: undefined })
                      }}
                    >
                      Période récente
                    </Button>
                  )}
                  <span>(vide = les {search.days} derniers jours complets)</span>
                </div>
                {(() => {
                  if (backtestQuery.isPending || !backtestQuery.data) {
                    return <p className="text-muted-foreground">Backtest en cours…</p>
                  }
                  if (backtestTree.length === 0) {
                    return (
                      <p className="text-muted-foreground">
                        {backtestQuery.data.excludedStoreCount > 0
                          ? `Aucune boutique n'a assez d'historique pour un backtest à cette date d'origine (${backtestQuery.data.excludedStoreCount} retirée(s)).`
                          : 'Pas assez de données pour un backtest sur ce périmètre.'}
                      </p>
                    )
                  }
                  return (
                    <ShortTermBacktest
                      stores={backtestTree}
                      metrics={backtestQuery.data.metrics}
                      startDate={backtestQuery.data.startDate}
                      endDate={backtestQuery.data.endDate}
                      excludedStoreCount={backtestQuery.data.excludedStoreCount}
                      formatDate={formatFrDate}
                    />
                  )
                })()}
              </TabsContent>
            </AppTabs>
          )}
        </div>

        {explained && (
          <ShortTermExplainModal
            storeId={explained.storeId}
            product={explained.product}
            storeLabel={storeLabelOf(explained.storeId)}
            days={forecastQuery.data?.horizonDays ?? search.days}
            onClose={() => setExplained(null)}
          />
        )}
      </AppContainer>
    </>
  )
}
